/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/**
 * Methods for querying and manipulating elements pertaining to a native
 * ToDesktop window shell.
 *
 * @remarks
 * This package exposes a number of methods for querying and manipulating
 * elements pertaining to a native ToDesktop window shell.
 *
 * @experimental
 * @public
 * @packageDocumentation
 */
import { checkIfCompatibleWithBackend, } from "@todesktop/client-util";
import { invoke, construct } from "./invoke.js";
import { createOnAndOffNamespaceListenersWithCompatiblityForLegacyEvents, } from "./utils.js";
/**
 * @public
 */
/**
 * Creates a new BrowserWindow.
 *
 * @param options - BrowserWindow constructor options.
 * @returns Identifier for the newly created BrowserWindow.
 * @public
 */
export async function create(options) {
    return construct({
        namespace: "BrowserWindow",
        args: [options],
    });
}
/**
 * Destroys the BrowserWindow corresponding to the `ref`.
 *
 * @param options - ref: Reference for the BrowserWindow to destroy
 * @public
 */
export async function destroy({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "destroy",
        args: [],
    });
}
/**
 * Sets a BrowserView on the reference BrowserWindow. If there are
 * other BrowserViews attached, they will be removed from the window.
 *
 * @param options - ref: Reference for the BrowserWindow; viewRef: Reference for the BrowserView to set.
 * @public
 */
export async function setBrowserView({ ref, viewRef, }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setBrowserView",
        args: [viewRef],
    });
}
/**
 * Adds a BrowserView to the reference BrowserWindow.
 *
 * @param options - ref: Reference for the BrowserWindow; viewRef: Reference for the BrowserView to add.
 * @public
 */
export async function addBrowserView({ ref, viewRef, }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "addBrowserView",
        args: [viewRef],
    });
}
/**
 * Removes a BrowserView from the reference BrowserWindow.
 *
 * @param options - ref: Reference for the BrowserWindow; viewRef: Reference for the BrowserView to remove.
 * @public
 */
export async function removeBrowserView({ ref, viewRef, }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "removeBrowserView",
        args: [viewRef],
    });
}
/**
 * Gets the width and height of the reference browser window
 *
 * @param options - ref: Reference for the BrowserWindow.
 * @public
 */
export async function getSize({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "getSize",
        args: [],
    });
}
/**
 * Changes the title of the reference BrowserWindow.
 *
 * @param options - ref: Reference for the BrowserWindow; title: Desired title for the BrowserWindow.
 * @public
 */
export async function setTitle({ ref, title }) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setTitle",
        args: [title],
    });
}
/**
 * Minimize the current window.
 *
 * @public
 */
export async function minimize() {
    return window["todesktop"].window.minimize();
}
/**
 * Unminimize the current window.
 *
 * @public
 */
export async function restore() {
    if (checkIfCompatibleWithBackend("nativeWindow:restore", "1.287.0")) {
        return window["todesktop"].window.restore();
    }
}
/**
 * Returns `true` if the current ToDesktop window is minimized.
 *
 * @public
 */
export async function isMinimized() {
    return window["todesktop"].window.isMinimized();
}
/**
 * Maximises the current ToDesktop window.
 *
 * @public
 */
export async function maximize() {
    return window["todesktop"].window.maximize();
}
/**
 * Minimizes the current ToDesktop window.
 *
 * @public
 */
export async function unmaximize() {
    return window["todesktop"].window.unmaximize();
}
/**
 * Returns `true` if the current ToDesktop window is maximized.
 *
 * @public
 */
export async function isMaximized() {
    return window["todesktop"].window.isMaximized();
}
/**
 * Enters fullscreen mode on the current ToDesktop window.
 *
 * @example
 * ```typescript
 * import { nativeWindow } from "@todesktop/client";
 *
 * const video: HTMLVideoElement;
 *
 * video.addEventListener("play", async () => {
 *   nativeWindow.enterFullScreen();
 * });
 * ```
 * @public
 */
export async function enterFullscreen() {
    return window["todesktop"].window.fullscreen();
}
/**
 * Exits fullscreen mode on the current ToDesktop window.
 *
 * @example
 * ```typescript
 * import { nativeWindow } from "@todesktop/client";
 *
 * const video: HTMLVideoElement;
 *
 * video.addEventListener("suspend", async () => {
 *   nativeWindow.exitFullScreen();
 * });
 * ```
 * @public
 */
export async function exitFullscreen() {
    return window["todesktop"].window.unfullscreen();
}
/**
 * Returns `true` if the current ToDesktop window is in fullscreen mode.
 *
 * @example
 * ```typescript
 * import { nativeWindow } from "@todesktop/client";
 *
 * if (nativeWindow.isFullscreen) {
 *   // change something in the UI
 * }
 * ```
 * @public
 */
export async function isFullscreen() {
    return window["todesktop"].window.isFullscreen();
}
/**
 * Causes the current window to be focussed on the user's desktop.
 *
 * @public
 */
export async function focus() {
    return window["todesktop"].window.focus();
}
/**
 * Removes the current window from focus on the user's desktop.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function blur({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "blur",
        args: [],
    });
}
/**
 * @public
 */
export async function areTabsSupported() {
    return window["todesktop"].window.areTabsSupported();
}
/**
 * Creates a new tab and loads the pre-defined app URL.
 *
 * @public
 */
export async function createNewTab() {
    return window["todesktop"].window.createNewTab();
}
/**
 * @param percent - An float between 0-1 will set the progress bar to a
 * certain percentage. e.g. `0.75` will show a progress bar 75% filled.
 *
 * Values above 1 will enable intermediate mode.
 *
 * Values below 0 will disable the progress bar.
 * @public
 */
export async function setProgressBar(percent) {
    return window["todesktop"].window.setProgressBar(percent);
}
/**
 * Returns a boolean indicating whether the window has been destroyed.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @public
 */
export async function isDestroyed({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "isDestroyed",
        args: [],
    });
}
/**
 * Returns a boolean indicating whether the window is visible to the user.
 *
 * @param options - ref: Reference for the BrowserWindow
 * @public
 */
export async function isVisible({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "isVisible",
        args: [],
    });
}
/**
 * Hides the BrowserWindow corresponding to the `ref`.
 *
 * @param options - ref: Reference for the BrowserWindow to hide
 * @public
 */
export async function hide({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "hide",
        args: [],
    });
}
/**
 * Shows the BrowserWindow corresponding to the `ref`.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function show({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "show",
        args: [],
    });
}
/**
 * Close the BrowserWindow corresponding to the `ref`.
 *
 * @param options - ref: Reference for the BrowserWindow to close
 * @public
 */
export async function close({ ref } = {}) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "close",
        args: [],
    });
}
/**
 * @param options - ref: Reference for the BrowserWindow to show
 * @returns An array of all opened browser windows.
 * @public
 */
export async function getAllWindows() {
    return (await invoke({
        namespace: "BrowserWindow",
        method: "getAllWindows",
        args: [],
        // TODO: Remove eslint-disable-next-line when You found a better type option other than "any"
        // eslint-disable-next-line
        //eslint-disable-next-line
    })).$;
}
/**
 * The title of the native window.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function getTitle({ ref } = {}) {
    return await invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        property: "title",
    });
}
/**
 * Reloads the web page.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function reload({ ref } = {}) {
    return await invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "reload",
        args: [],
    });
}
/**
 * Get a WebContents reference for a BrowserWindow. WebContents is responsible
 * for rendering and controlling a web page
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function getWebContents({ ref } = {}) {
    return (await invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        property: "webContents",
    }));
}
/**
 * Sets whether the window should show always on top of other windows. After
 * setting this, the window is still a normal window, not a toolbox window which
 * can not be focused on.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function setAlwaysOnTop({ ref } = {}, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setAlwaysOnTop",
        args,
    });
}
/**
 * Sets whether the window should be visible on all workspaces.
 *
 * **Note:** This API does nothing on Windows.
 *
 * @platform darwin,linux
 */
export async function setVisibleOnAllWorkspaces({ ref }, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setVisibleOnAllWorkspaces",
        args,
    });
}
/**
 * Changes window icon.
 *
 * @see https://www.electronjs.org/docs/latest/api/browser-window#winseticonicon-windows-linux
 * @public
 */
export async function setIcon({ ref }, image) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setIcon",
        args: [image],
    });
}
/**
 * Prevents the window contents from being captured by other apps.
 *
 * @param options - ref: Reference for the BrowserWindow to show
 * @public
 */
export async function setContentProtection({ ref } = {}, ...args) {
    return invoke({
        instanceRef: ref,
        namespace: "BrowserWindow",
        method: "setContentProtection",
        args,
    });
}
const _legacyNativeWindowEventMappings = {
    enterFullscreen: "window.enter-full-screen",
    exitFullscreen: "window.leave-full-screen",
};
const listeners = createOnAndOffNamespaceListenersWithCompatiblityForLegacyEvents(_legacyNativeWindowEventMappings, "BrowserWindow");
/**
 * @public
 */
export const on = listeners.on;
/**
 * @public
 */
export const removeAllListeners = listeners.removeAllListeners;
